import * as Sentry from "@sentry/browser";
import { getDoc, getDocs, setDoc, updateDoc, addDoc, collection, doc, query, where } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { auth, firestore } from "$lib/firebase/client";
import { getDocument, getCollection } from "$lib/firebase/firestore";
import { UserNotFound } from "$lib/lib/errors";
import { User } from "$lib/types/User";

export const ERROR_CODES = {
  SUBSCRIPTION_EXPIRED: 10,
  UNKNOWN: 100,
};

export const ERROR_MESSAGES: { [K in keyof typeof ERROR_CODES]: string } = {
  SUBSCRIPTION_EXPIRED: "Your subscription seems to have expired.",
  UNKNOWN: "An unknown error occurred.",
};

function isAlreadyExistsError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  if (code === "already-exists") return true;
  return typeof message === "string" && /already exists/i.test(message);
}

export class AccountService {
  static instance: AccountService | null = null;

  constructor() {}

  isSubscriptionExpired(user: any): boolean {
    const roles = Object.values(user.roles ?? {});
    if (!user.migrated && roles.some((role: unknown) => typeof role === 'string' && role.includes("subscriber"))) {
      return false;
    }
    if (user.hasActiveSubscription && user.hasActiveSubscription()) {
      const expirationDate = new Date(user.expirationDate);
      const now = new Date();
      if (expirationDate < now) {
        return true;
      }
    }
    this.logOut(ERROR_CODES.SUBSCRIPTION_EXPIRED);
    return true;
  }

  loginWithJwt(): never {
    throw new Error("This is deprecated.");
  }

  async authStateChanged(u: any): Promise<User | null> {
    if (u == null) {
      return null;
    }

    Sentry.setContext("user", {
      uid: u.uid,
    });

    if (!firestore) throw new Error('Firestore not initialised');

    const userDoc = await getDoc(doc(firestore, "users", u.uid));
    let user: User;
    if (!userDoc.exists()) {
      throw new UserNotFound();
    } else {
      user = User.fromJson(u.uid, userDoc.data());
    }

    if (user.hasValidSubscription() && await this.maybeForwardToTarget(u.uid)) {
      return null;
    }

    return user;
  }

  async upsertUserDate(u: any, token: { displayName: string; email: string; roles: Record<string, string> }): Promise<any> {
    if (!firestore) throw new Error('Firestore not initialised');

    const userRef = doc(firestore, "users", u.uid);
    let userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      await setDoc(userRef, {
        name: token.displayName,
        email: token.email,
        roles: token.roles,
        type: "student",
      });
    } else {
      await updateDoc(userRef, {
        name: token.displayName,
        email: token.email,
        roles: token.roles,
      });
    }
    userSnap = await getDoc(userRef);
    return userSnap.data();
  }

  getJwt(user: any): any {
    if (user) {
      const token = user.accessToken;
      if (!token) {
        throw new Error("No access token available");
      }
      var base64Url = token.split(".")[1];
      var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      var jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      return JSON.parse(jsonPayload);
    }

    throw new Error("User is not logged in");
  }

  async maybeForwardToTarget(uid: string): Promise<boolean> {
    if (document.cookie.includes("target")) {
      const cookieString = document.cookie;
      const cookieArray = cookieString.split("; ");
      const cookieObject: Record<string, string> = {};
      cookieArray.forEach((cookie) => {
        const index = cookie.indexOf("=");
        if (index > -1) {
          const key = cookie.substring(0, index).trim();
          const value = cookie.substring(index + 1).trim();
          cookieObject[key] = value;
        }
      });
      const target = cookieObject['target'];
      if (!target) {
        return false;
      }
      let decodedTarget: { expires: string; host?: string };
      try {
        decodedTarget = JSON.parse(atob(decodeURIComponent(target)));
      } catch (e) {
        console.error(e);
        Sentry.captureMessage("Failed to decode target", {
          extra: { target, error: e },
        });
        document.cookie = "target=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        return false;
      }
      if (new Date(decodedTarget.expires).getTime() < Date.now()) {
        document.cookie = "target=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        return false;
      }
      const allowedDomains = [
        "universalvoice.nl",
        "uvi-portaal.pages.dev",
        "localhost",
        "universalvoice.eu"
      ];
      if (
        decodedTarget?.host &&
        allowedDomains.some((domain) => decodedTarget.host!.includes(domain))
      ) {
        if (!firestore) throw new Error('Firestore not initialised');
        const code =
          Math.random().toString(36).substring(2, 15) +
          Math.random().toString(36).substring(2, 15);
        try {
          await addDoc(collection(firestore, "authCodes"), {
            code: code,
            uid: uid,
            expires: new Date().getTime() + 1000 * 30,
          });
        } catch (error) {
          if (!isAlreadyExistsError(error)) {
            console.error(error);
            Sentry.captureException(error);
            return false;
          }
        }
        document.cookie = "target=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        let mlQueryParam = "&multiLangEnabled=true";
        window.location.href = `https://${decodedTarget.host}?code=${code}${mlQueryParam}`;
        return true;
      }
      document.cookie = "target=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      return false;
    }
    return false;
  }

  logOut(messageCode?: number): void {
    if (!auth) return;
    signOut(auth).then(
      () => {
        let suffix = "";
        if (messageCode) {
          suffix = `?error=${messageCode}`;
        }
        window.location.href = `/${suffix}`;
      },
      (error: any) => {
        console.error(error);
      }
    );
  }

  static getInstance(): AccountService {
    if (AccountService.instance == null) {
      AccountService.instance = new AccountService();
    }
    return AccountService.instance;
  }
}
